// src/utils/cloudinary.js

import { TEMPLATE_CONFIG } from '../config/template.config';

const CLOUD_NAME = TEMPLATE_CONFIG.cloudinary.cloudName;

/**
 * Clean and sanitize Cloudinary public IDs
 * Removes spaces, parentheses, and other problematic characters
 */
export const cleanPublicId = (publicId) => {
  if (!publicId) return '';
  
  // If it's already a full URL, return as-is
  if (publicId.startsWith('http')) {
    return publicId;
  }
  
  return publicId
    .trim()
    // Remove file extensions (Cloudinary handles this)
    .replace(/\.(jpg|jpeg|png|gif|webp)$/i, '')
    // Replace spaces with underscores
    .replace(/\s+/g, '_')
    // Remove parentheses
    .replace(/[()]/g, '')
    // Remove "- Copy" suffixes
    .replace(/[-_]Copy/gi, '_')
    // Remove multiple consecutive underscores
    .replace(/_+/g, '_')
    // Remove trailing underscores/hyphens
    .replace(/[_-]+$/g, '')
    // Remove leading underscores/hyphens
    .replace(/^[_-]+/g, '');
};

/**
 * Generate optimized Cloudinary URL with automatic ID cleaning
 */
export const getCloudinaryUrl = (publicId, options = {}) => {
  if (!publicId) return '';

  // If it's already a full URL (e.g. external), return it as is
  if (publicId.startsWith('http')) {
    return publicId;
  }

  if (!CLOUD_NAME) {
    console.error('❌ Cloud Name not found. Check REACT_APP_CLOUDINARY_CLOUD_NAME in .env.local');
    return '';
  }
  
  // ⭐ CRITICAL: Clean the public ID first
  const cleanedId = cleanPublicId(publicId);
  
  const {
    width = 'auto',
    height = null,
    quality = 'auto',
    format = 'auto',
    crop = 'fill',
    gravity = 'auto'
  } = options;
  
  const transformations = [
    `w_${width}`,
    height ? `h_${height}` : null,
    `q_${quality}`,
    `f_${format}`,
    `c_${crop}`,
    gravity !== 'auto' ? `g_${gravity}` : null
  ].filter(Boolean).join(',');
  
  return `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/${transformations}/${cleanedId}`;
};

/**
 * Generate URL without automatic cleaning (for when IDs are already clean)
 */
export const getCloudinaryUrlRaw = (publicId, options = {}) => {
  if (!publicId) return '';
  if (publicId.startsWith('http')) return publicId;
  if (!CLOUD_NAME) {
    console.error('❌ Cloud Name not found');
    return '';
  }
  
  // Remove extension but don't clean
  const baseId = publicId.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '');
  
  const {
    width = 'auto',
    height = null,
    quality = 'auto',
    format = 'auto',
    crop = 'fill',
    gravity = 'auto'
  } = options;
  
  const transformations = [
    `w_${width}`,
    height ? `h_${height}` : null,
    `q_${quality}`,
    `f_${format}`,
    `c_${crop}`,
    gravity !== 'auto' ? `g_${gravity}` : null
  ].filter(Boolean).join(',');
  
  return `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/${transformations}/${baseId}`;
};

/**
 * Preset sizes for common use cases
 */
export const CloudinaryPresets = {
  thumbnail: (publicId) => getCloudinaryUrl(publicId, { 
    width: 300, 
    quality: 80, 
    crop: 'fill'
  }),
  
  gallery: (publicId) => getCloudinaryUrl(publicId, { 
    width: 800, 
    quality: 85, 
    crop: 'fill'
  }),
  
  hero: (publicId) => getCloudinaryUrl(publicId, { 
    width: 1920, 
    quality: 90, 
    crop: 'fill'
  }),
  
  lightbox: (publicId) => getCloudinaryUrl(publicId, { 
    width: 2400, 
    quality: 90, 
    crop: 'limit' // Don't crop in lightbox
  })
};

/**
 * Debug helper - logs the URL being generated
 */
export const getCloudinaryUrlDebug = (publicId, options = {}) => {
  const url = getCloudinaryUrl(publicId, options);
  console.log('🔍 Cloudinary URL Debug:');
  console.log('   Input:', publicId);
  console.log('   Cleaned:', cleanPublicId(publicId));
  console.log('   Final URL:', url);
  return url;
};